import React, { useState, useEffect, useRef } from 'react';
import { formationService } from '../../services/formationService';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Image } from 'primereact/image';
import { Checkbox } from 'primereact/checkbox';
import { FileUpload } from 'primereact/fileupload';
import { HiPlus, HiTrash, HiPencil, HiSave, HiClipboardList, HiOutlinePhotograph, HiChevronLeft } from 'react-icons/hi';
import './styles/FormationManagement.css';

function FormationManagement({ editionYear }) {
  const [settings, setSettings] = useState({
    title: 'NOS FORMATIONS',
    subtitle: '',
    is_visible: true
  });
  const [formations, setFormations] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormationDialog, setShowFormationDialog] = useState(false);
  const [editingFormation, setEditingFormation] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [view, setView] = useState('list'); // 'list' (formations) or 'registrations'
  const [uploadingImage, setUploadingImage] = useState(false);
  const toast = useRef(null);

  useEffect(() => {
    loadData();
  }, [editionYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      const settingsData = await formationService.getSettings(editionYear);
      setSettings(settingsData);
      
      const formationList = await formationService.getFormations(editionYear);
      setFormations(formationList);

      const registrationList = await formationService.getRegistrations(editionYear);
      setRegistrations(registrationList);
    } catch (error) {
      console.error('Error loading formation management data:', error);
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Impossible de charger les données' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await formationService.updateSettings(settings);
      // Dispatch custom event to notify Header
      window.dispatchEvent(new CustomEvent('formationSettingsUpdated'));
      if (toast.current) {
        toast.current.show({ severity: 'success', summary: 'Succès', detail: 'Paramètres enregistrés' });
      }
    } catch (error) {
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Échec de l\'enregistrement' });
      }
    }
  };

  const handleEditFormation = (formation) => {
    setEditingFormation({ ...formation });
    setIsAdding(false);
    setShowFormationDialog(true);
  };

  const handleAddFormation = () => {
    setEditingFormation({
      title: '',
      description: '',
      details: '',
      image_url: '',
      image_path: '',
      price: 80,
      display_order: formations.length + 1,
      is_active: true,
      edition_id: settings.edition_id
    });
    setIsAdding(true);
    setShowFormationDialog(true);
  };

  const handleSaveFormation = async () => {
    try {
      await formationService.saveFormation(editingFormation);
      await loadData();
      setShowFormationDialog(false);
      setEditingFormation(null);
      if (toast.current) {
        toast.current.show({ severity: 'success', summary: 'Succès', detail: 'Formation enregistrée' });
      }
    } catch (error) {
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Échec de l\'enregistrement' });
      }
    }
  };

  const handleDeleteFormation = async (formation) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette formation ?')) {
      try {
        if (formation.image_path) {
          await formationService.deleteFormationFile(formation.image_path);
        }
        await formationService.deleteFormation(formation.id);
        await loadData();
        if (toast.current) {
          toast.current.show({ severity: 'success', summary: 'Succès', detail: 'Formation supprimée' });
        }
      } catch (error) {
        if (toast.current) {
          toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Échec de la suppression' });
        }
      }
    }
  };

  const handleDeleteRegistration = async (id) => {
    if (window.confirm('Supprimer cette inscription ?')) {
      try {
        await formationService.deleteRegistration(id);
        await loadData();
        if (toast.current) {
          toast.current.show({ severity: 'success', summary: 'Succès', detail: 'Inscription supprimée' });
        }
      } catch (error) {
        if (toast.current) {
          toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Échec de la suppression' });
        }
      }
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.files[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      
      // Delete old image if it exists
      if (editingFormation.image_path) {
        await formationService.deleteFormationFile(editingFormation.image_path);
      }

      const result = await formationService.uploadFormationImage(file, editionYear);
      setEditingFormation(prev => ({
        ...prev,
        image_url: result.url,
        image_path: result.path
      }));
      
      if (toast.current) {
        toast.current.show({ severity: 'success', summary: 'Image', detail: 'Téléchargée avec succès' });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Échec du téléchargement' });
      }
    } finally {
      setUploadingImage(false);
    }
  };

  const statusBodyTemplate = (rowData) => {
    return <Tag value={rowData.is_active ? 'Actif' : 'Inactif'} severity={rowData.is_active ? 'success' : 'danger'} />;
  };

  const imageBodyTemplate = (rowData) => {
    return <Image src={rowData.image_url} alt={rowData.title} width="60" preview style={{ borderRadius: '8px', overflow: 'hidden' }} />;
  };

  const dateBodyTemplate = (rowData) => {
    return new Date(rowData.created_at).toLocaleDateString();
  };

  const priceBodyTemplate = (rowData) => {
     return <span className="font-bold">{rowData.price}€</span>;
  };

  const formationActionsTemplate = (rowData) => {
    return (
      <div className="flex gap-2">
        <Button icon={<HiPencil />} className="p-button-rounded p-button-info p-button-outlined" onClick={() => handleEditFormation(rowData)} title="Modifier" />
        <Button icon={<HiTrash />} className="p-button-rounded p-button-danger p-button-outlined" onClick={() => handleDeleteFormation(rowData)} title="Supprimer" />
      </div>
    );
  };

  const registrationActionsTemplate = (rowData) => {
    return (
      <Button icon={<HiTrash />} className="p-button-rounded p-button-danger p-button-outlined" onClick={() => handleDeleteRegistration(rowData.id)} title="Supprimer l'inscription" />
    );
  };

  if (loading && !showFormationDialog) return <div className="p-4 text-center">Chargement...</div>;

  return (
    <div className="formation-management admin-section">
      <Toast ref={toast} />
      
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-bold flex items-center gap-2" style={{ fontSize: '1.5rem', color: '#1a202c' }}>
            {view === 'registrations' && <HiChevronLeft className="cursor-pointer text-gray-400" onClick={() => setView('list')} />}
            Training & Formations <span className="text-red-600">[{editionYear}]</span>
        </h2>
        <div className="header-actions">
          {view === 'list' ? (
            <>
              <Button 
                label="Inscriptions" 
                icon={<HiClipboardList className="mr-2" />} 
                className="p-button-secondary p-button-outlined" 
                onClick={() => setView('registrations')} 
              />
              <Button 
                label="Ajouter" 
                icon={<HiPlus className="mr-2" />} 
                className="p-button-danger"
                onClick={handleAddFormation} 
              />
            </>
          ) : (
            <Button 
                label="Listes des Sessions" 
                icon={<HiChevronLeft className="mr-2" />} 
                className="p-button-secondary" 
                onClick={() => setView('list')} 
            />
          )}
        </div>
      </div>

      {view === 'list' && (
        <div className="mgmt-grid">
          {/* Settings Card */}
          <div className="mgmt-card">
            <h3 className="mgmt-card-title">Configuration de la Page</h3>
            <div className="mgmt-form-grid">
              <div className="flex flex-column gap-2 mb-3">
                <label className="form-label">Titre de la Section</label>
                <InputText value={settings.title} onChange={(e) => setSettings({...settings, title: e.target.value})} className="w-full" />
              </div>
              <div className="flex flex-column gap-2 mb-3">
                <label className="form-label">Sous-titre / Accroche</label>
                <InputText value={settings.subtitle} onChange={(e) => setSettings({...settings, subtitle: e.target.value})} className="w-full" />
              </div>
              <div className="flex items-center gap-2 mt-2 col-span-full">
                <Checkbox inputId="isVisible" checked={settings.is_visible} onChange={(e) => setSettings({...settings, is_visible: e.checked})} />
                <label htmlFor="isVisible" className="cursor-pointer font-medium p-checkbox-label">Rendre cette section visible sur le site public</label>
              </div>
              <div className="flex justify-end mt-4 col-span-full">
                <Button label="Enregistrer les modifications" icon={<HiSave className="mr-2" />} onClick={handleSaveSettings} className="p-button-danger bg-red-600 border-none px-4" />
              </div>
            </div>
          </div>

          {/* Formations List */}
          <div className="mgmt-card">
            <h3 className="mgmt-card-title">Planning des Sessions</h3>
            <DataTable 
                value={formations} 
                responsiveLayout="scroll" 
                className="p-datatable-sm" 
                stripedRows 
                emptyMessage="Aucun enregistrement trouvé."
                rowHover
            >
              <Column field="display_order" header="Ordre" style={{ width: '5rem' }} sortable />
              <Column header="Image" body={imageBodyTemplate} style={{ width: '6rem' }} />
              <Column field="title" header="Titre Training" sortable />
              <Column field="price" header="Tarif" body={priceBodyTemplate} sortable style={{ width: '8rem' }} />
              <Column field="is_active" header="Etat" body={statusBodyTemplate} sortable style={{ width: '8rem' }} />
              <Column header="Actions" body={formationActionsTemplate} style={{ width: '10rem' }} />
            </DataTable>
          </div>
        </div>
      )}

      {view === 'registrations' && (
        <div className="mgmt-card">
          <h3 className="mgmt-card-title registration-title">Suivi des Inscriptions en temps réel</h3>
          <DataTable 
            value={registrations} 
            responsiveLayout="scroll" 
            className="p-datatable-sm" 
            paginator 
            rows={10} 
            stripedRows 
            emptyMessage="Aucune inscription enregistrée pour le moment."
            rowHover
          >
            <Column field="created_at" header="Date" body={dateBodyTemplate} sortable />
            <Column field="formations.title" header="Session Choisie" sortable />
            <Column header="Participant" body={(r) => `${r.nom} ${r.prenom}`} sortable />
            <Column header="Contact" body={(r) => (
              <div className="text-sm">
                <div style={{ fontWeight: 600 }}>{r.email}</div>
                <div style={{ color: '#718096', fontSize: '0.85rem' }}>{r.telephone}</div>
              </div>
            )} />
            <Column field="organisation" header="Institution / Organisation" sortable />
            <Column header="Actions" body={registrationActionsTemplate} />
          </DataTable>
        </div>
      )}

      {/* Formation Dialog */}
      <Dialog 
        header={isAdding ? "✨ Nouvelle Session de Formation" : "📝 Éditer la Session"} 
        visible={showFormationDialog} 
        style={{ width: '60vw' }} 
        modal 
        onHide={() => setShowFormationDialog(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Annuler" icon={<HiChevronLeft className="mr-2" />} className="p-button-text p-button-secondary" onClick={() => setShowFormationDialog(false)} />
            <Button label="Sauvegarder" icon={<HiSave className="mr-2" />} className="p-button-primary" onClick={handleSaveFormation} />
          </div>
        }
      >
        {editingFormation && (
          <div className="flex flex-column gap-3 mt-2">
            <div className="flex flex-column gap-2 mb-2">
              <label className="form-label">Titre Intitulé</label>
              <InputText value={editingFormation.title} onChange={(e) => setEditingFormation({...editingFormation, title: e.target.value})} className="w-full" />
            </div>
            
            <div className="flex flex-column gap-2 mb-2">
              <label className="form-label">Description Courte (Aperçu carte)</label>
              <InputTextarea value={editingFormation.description} onChange={(e) => setEditingFormation({...editingFormation, description: e.target.value})} rows={2} className="w-full" />
            </div>
            
            <div className="flex flex-column gap-2 mb-2">
              <label className="form-label">Détails de la Formation (Contenu du modal)</label>
              <InputTextarea value={editingFormation.details} onChange={(e) => setEditingFormation({...editingFormation, details: e.target.value})} rows={6} className="w-full" />
            </div>
            
            <div className="mgmt-form-grid mb-2">
              <div className="flex flex-column gap-2">
                <label className="form-label">Prix unitaire (€)</label>
                <InputNumber value={editingFormation.price} onValueChange={(e) => setEditingFormation({...editingFormation, price: e.value})} mode="currency" currency="EUR" locale="de-DE" className="w-full" />
              </div>
              <div className="flex flex-column gap-2">
                <label className="form-label">Position d'Affichage</label>
                <InputNumber value={editingFormation.display_order} onValueChange={(e) => setEditingFormation({...editingFormation, display_order: e.value})} className="w-full" />
              </div>
            </div>

            <div className="upload-preview-area mb-3">
               <div className="preview-img-container">
                  {editingFormation.image_url ? (
                    <Image src={editingFormation.image_url} alt="Current Thumbnail" width="120" preview />
                  ) : (
                    <HiOutlinePhotograph size={40} color="#cbd5e0" />
                  )}
               </div>
               <div className="flex-1">
                  <label className="form-label">Illustration visuelle</label>
                  <FileUpload 
                    mode="basic" 
                    name="image" 
                    accept="image/*" 
                    maxFileSize={5000000} 
                    customUpload 
                    uploadHandler={handleImageUpload} 
                    auto 
                    chooseLabel={uploadingImage ? "Patientez..." : "Parcourir & Télécharger"} 
                    className="p-button-outlined w-full"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#718096', marginTop: '0.5rem', fontStyle: 'italic' }}>
                    Le fichier sera automatiquement optimisé pour le stockage Cloud.
                  </p>
               </div>
            </div>

            <div className="flex items-center gap-2 mt-2">
              <Checkbox inputId="isActive" checked={editingFormation.is_active} onChange={(e) => setEditingFormation({...editingFormation, is_active: e.checked})} />
              <label htmlFor="isActive" className="cursor-pointer font-semibold p-checkbox-label">Publier immédiatement (Activé)</label>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}

export default FormationManagement;
